// A critically-damped-by-default spring, driven by (damping,
// response) the way Apple's fluid-interfaces model describes it,
// rather than raw stiffness/mass. It's intentionally imperative:
// values are pushed to a caller-supplied onUpdate callback (which
// typically mutates a DOM ref's style directly) instead of living
// in React state, so a 60fps drag never triggers a React re-render.

import { useRef, useCallback, useEffect, useMemo } from 'react';

export interface SpringOptions {
  damping?: number;
  response?: number;
  velocity?: number;
  onUpdate?: (value: number) => void;
  onSettle?: () => void;
}

export interface SpringController {
  setTarget: (target: number, velocity?: number) => void;
  jumpTo: (value: number) => void;
  stop: () => void;
  getValue: () => number;
  setVelocity: (velocity: number) => void;
}

export function useSpring(initialValue: number, options: SpringOptions = {}): SpringController {
  const valueRef = useRef(initialValue);
  const targetRef = useRef(initialValue);
  const velocityRef = useRef(options.velocity ?? 0);
  const dampingRef = useRef(options.damping ?? 1);
  const responseRef = useRef(options.response ?? 0.4);
  const rafRef = useRef<number | null>(null);
  const lastRef = useRef<number | null>(null);
  const onUpdateRef = useRef(options.onUpdate);
  const onSettleRef = useRef(options.onSettle);

  // Keep the latest callbacks/tuning without restarting the loop.
  onUpdateRef.current = options.onUpdate;
  onSettleRef.current = options.onSettle;
  if (options.damping !== undefined) dampingRef.current = options.damping;
  if (options.response !== undefined) responseRef.current = options.response;

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastRef.current = null;
  }, []);

  const tick = useCallback(
    (now: number) => {
      const last = lastRef.current ?? now;
      const dt = Math.min((now - last) / 1000, 0.032);
      lastRef.current = now;

      const angularFrequency = (2 * Math.PI) / responseRef.current;
      const c = 2 * dampingRef.current * angularFrequency;
      const k = angularFrequency * angularFrequency;
      const displacement = valueRef.current - targetRef.current;
      const acceleration = -k * displacement - c * velocityRef.current;

      velocityRef.current += acceleration * dt;
      valueRef.current += velocityRef.current * dt;
      onUpdateRef.current?.(valueRef.current);

      const settled = Math.abs(valueRef.current - targetRef.current) < 0.02 && Math.abs(velocityRef.current) < 0.02;
      if (settled) {
        valueRef.current = targetRef.current;
        velocityRef.current = 0;
        onUpdateRef.current?.(valueRef.current);
        stop();
        onSettleRef.current?.();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [stop],
  );

  const start = useCallback(() => {
    if (rafRef.current !== null) return;
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const setTarget = useCallback(
    (target: number, velocity?: number) => {
      targetRef.current = target;
      if (velocity !== undefined) velocityRef.current = velocity;
      start();
    },
    [start],
  );

  const jumpTo = useCallback(
    (value: number) => {
      stop();
      valueRef.current = value;
      targetRef.current = value;
      velocityRef.current = 0;
      onUpdateRef.current?.(value);
    },
    [stop],
  );

  const setVelocity = useCallback((velocity: number) => {
    velocityRef.current = velocity;
  }, []);

  const getValue = useCallback(() => valueRef.current, []);

  useEffect(() => stop, [stop]);

  return useMemo(
    () => ({ setTarget, jumpTo, stop, getValue, setVelocity }),
    [setTarget, jumpTo, stop, getValue, setVelocity],
  );
}
